import React, { useState, useEffect } from 'react';
import { useTonConnectUI, useTonAddress } from '@tonconnect/ui-react';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

const API_BASE_URL = (process.env as any).REACT_APP_API_URL || 'http://localhost:8000';

const BalanceScreen = () => {
  const [tonConnectUI] = useTonConnectUI();
  const walletAddress = useTonAddress();
  const { t, language } = useLanguage();
  
  // Получаем user_id из Telegram WebApp или используем demo для локальной разработки
  const userId = (window as any)?.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString() || 'demo';
  
  const [balance, setBalance] = useState<number>(0);
  const [depositAmount, setDepositAmount] = useState<string>('');
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Загружаем баланс при монтировании компонента
  useEffect(() => {
    loadBalance();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBalance = async (retryCount = 3): Promise<void> => {
    try {
      console.log('📡 Загрузка баланса...', { userId, retryCount });
      
      // ✅ ЧИТАЕМ start_param из URL HASH (бот передает через #tgWebAppStartParam=123)
      const webApp = (window as any)?.Telegram?.WebApp;
      let startParam = webApp?.initDataUnsafe?.start_param; // Пробуем стандартный способ
      
      // Если не нашли в initDataUnsafe, читаем из URL hash
      if (!startParam && window.location.hash) {
        const match = window.location.hash.match(/tgWebAppStartParam=(\d+)/);
        if (match) {
          startParam = match[1];
          console.log('🔗 start_param найден в URL hash:', startParam);
        }
      }
      
      const params: any = { wallet_address: userId };
      
      if (startParam) {
        const referrerId = parseInt(startParam);
        params.referrer_id = referrerId;
        
        // ✅ СОХРАНЯЕМ referrer_id в localStorage для использования при покупке сундуков
        if (!localStorage.getItem('referrer_id')) {
          localStorage.setItem('referrer_id', startParam);
          console.log('💾 referrer_id сохранен в localStorage:', startParam);
        }
        
        console.log('🔗 Реферальная ссылка обнаружена! Referrer ID:', referrerId);
        console.log('📤 Передаем referrer_id в /user/balance:', referrerId);
      } else {
        console.log('❌ start_param не найден - пользователь открыл бот напрямую');
      }
      
      const response = await axios.get(`${API_BASE_URL}/user/balance`, {
        params,
        timeout: 30000 // 30 секунд
      });
      console.log('✅ Баланс загружен:', response.data);
      setBalance(response.data.balance || 0);
    } catch (err: any) {
      console.error('❌ Ошибка загрузки баланса:', {
        message: err.message,
        code: err.code,
        response: err.response?.data,
        retryCount
      });
      
      // Повторная попытка при таймауте или сетевых ошибках
      if (retryCount > 0 && (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK' || !err.response)) {
        console.log(`🔄 Повторная попытка через 2 сек... (осталось попыток: ${retryCount})`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return loadBalance(retryCount - 1);
      }
      
      setBalance(0);
    }
  };

  const handleConnectWallet = async () => {
    try {
      await tonConnectUI.openModal();
    } catch (err) {
      setError('Ошибка подключения кошелька');
    }
  };

  const handleDisconnectWallet = async () => {
    try {
      await tonConnectUI.disconnect();
      setBalance(0);
    } catch (err) {
      setError('Ошибка отключения кошелька');
    }
  };

  const validateAmount = (amount: string): boolean => {
    const num = parseFloat(amount);
    if (isNaN(num) || num <= 0) {
      setError('Введите корректную сумму больше 0');
      return false;
    }
    return true;
  };

  const handleDeposit = async () => {
    setError('');
    setSuccess('');

    console.log('🔧 DEBUG handleDeposit started');
    console.log('🔧 userId:', userId);
    console.log('🔧 API_BASE_URL:', API_BASE_URL);
    console.log('🔧 walletAddress:', walletAddress);
    console.log('🔧 depositAmount:', depositAmount);

    if (!walletAddress) {
      setError('Сначала подключите кошелёк для пополнения');
      return;
    }

    if (!validateAmount(depositAmount)) {
      return;
    }

    const amount = parseFloat(depositAmount);

    // Проверка минимальной суммы депозита
    if (amount < 0.5) {
      setError('Минимальная сумма депозита: 0.5 TON');
      return;
    }

    setLoading(true);

    try {
      // ✅ Получаем адрес кошелька для депозита (с умной ротацией на основе user_id)
      console.log('💳 Запрашиваем адрес кошелька для депозита...');
      const addressResponse = await axios.get(`${API_BASE_URL}/deposit/address`, {
        params: { wallet_address: userId }
      });
      const depositAddress = addressResponse.data.address;
      console.log('✅ Получен адрес для депозита:', depositAddress);
      
      // Отправка TON транзакции через TON Connect
      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 600, // 10 минут
        messages: [
          {
            address: depositAddress, // Адрес с ротацией из backend
            amount: (amount * 1e9).toString() // Конвертация в nanoTON
            // payload убран - используем pending deposits для связи транзакции с пользователем
          }
        ]
      };

      await tonConnectUI.sendTransaction(transaction);
      
      console.log('✅ TON транзакция успешно отправлена');
      console.log('📝 Создаем pending deposit для userId:', userId, 'amount:', amount);
      
      // Создаем заявку на pending deposit
      try {
        const response = await axios.post(`${API_BASE_URL}/deposit/pending`, {
          wallet_address: userId,  // Telegram ID
          ton_address: walletAddress,  // TON кошелек пользователя
          amount: amount
        });
        console.log('✅ Pending deposit создан:', response.data);
        setSuccess(`Депозит ${amount} TON отправлен! Ожидайте подтверждения администратора.`);
      } catch (err: any) {
        console.error('❌ Error creating pending deposit:', err);
        console.error('❌ Response data:', err?.response?.data);
        console.error('❌ Status:', err?.response?.status);
        
        // Показываем ошибку пользователю
        const errorMsg = err?.response?.data?.detail || err?.message || 'Неизвестная ошибка';
        setError(`⚠️ TON отправлен, но ошибка создания заявки: ${errorMsg}. Свяжитесь с администратором!`);
      }
      
      setDepositAmount('');
      
      // Синхронизируем баланс с backend
      setTimeout(loadBalance, 2000);
    } catch (err: any) {
      setError(err?.message || 'Ошибка отправки транзакции');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    setError('');
    setSuccess('');

    if (!walletAddress) {
      setError('Сначала подключите кошелёк для вывода средств');
      return;
    }

    if (!validateAmount(withdrawAmount)) {
      return;
    }

    const amount = parseFloat(withdrawAmount);

    if (amount > balance) {
      setError(`Недостаточно средств. Доступно: ${balance.toFixed(2)} TON`);
      return;
    }

    setLoading(true);

    // Проверка минимальной суммы
    if (amount < 1) {
      setError('Минимальная сумма вывода: 1 TON');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/withdrawal/create`, {
        wallet_address: userId,
        ton_address: walletAddress, // TON адрес для вывода
        amount: amount
      });

      const amountToReceive = response.data.amount_to_receive || (amount * 0.93);
      const fee = response.data.fee || (amount * 0.07);
      const newBalance = response.data.new_balance;
      
      // ✅ Обновляем баланс сразу из ответа (средства уже списаны на backend)
      if (newBalance !== undefined) {
        setBalance(newBalance);
        console.log(`💰 Баланс обновлен после создания withdrawal: ${newBalance} TON`);
      } else {
        // Fallback: загружаем баланс из API
        await loadBalance();
      }
      
      setSuccess(`✅ Заявка на вывод создана!\n💰 Сумма: ${amount} TON\n💸 Комиссия: ${fee.toFixed(2)} TON (7%)\n✅ К получению: ${amountToReceive.toFixed(2)} TON\n⏳ Ожидайте вывод в течении 24 часов.`);
      setWithdrawAmount('');
    } catch (err: any) {
      let errorMsg = err?.response?.data?.detail || 'Ошибка создания заявки на вывод';
      
      // Обработка ошибки 24-часового ограничения (формат: "23h 40m")
      if (errorMsg.includes('Withdrawal available in') && errorMsg.includes('after deposit')) {
        const match = errorMsg.match(/(\d+)h (\d+)m/);
        if (match) {
          const hours = parseInt(match[1]);
          const minutes = parseInt(match[2]);
          // ✅ ИСПРАВЛЕНО: правильная логика языков
          errorMsg = language === 'en' 
            ? `⏱️ Withdrawal available in ${hours}h ${minutes}m`
            : `⏱️ Вывод будет доступен через ${hours} ч. ${minutes} мин.`;
        }
      }
      
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="pb-20 min-h-screen bg-cover bg-center bg-fixed"
      style={{ backgroundImage: 'url(/assets/treasures-bg.jpg)' }}
    >
      {/* Фиксированный header с кнопкой поддержки */}
      <div className="sticky top-0 z-10 bg-gradient-to-b from-purple-900/95 to-purple-800/90 backdrop-blur-sm border-b border-white/20 shadow-lg">
        <div className="flex items-center justify-center px-4 py-3">
          <button
            onClick={() => window.open('https://t.me/treasuretonsup', '_blank')}
            className="flex items-center gap-2 px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200 text-white font-semibold border border-white/30"
          >
            <span className="text-xl">💬</span>
            <span>{t.supportButton}</span>
          </button>
        </div>
      </div>

      <div className="space-y-4 mt-4 px-3">
        {/* Баланс пользователя - всегда отображается */}
        <div className="card bg-white/10 text-white">
          <div className="text-2xl font-bold mb-4 text-center">
            {t.balanceTitle}
          </div>
          
          <div className="bg-white/10 rounded-lg p-4 text-center">
            <div className="text-sm opacity-80 mb-1">{t.availableBalance}</div>
            <div className="text-3xl font-bold text-yellow-400">{balance.toFixed(2)} TON</div>
          </div>
          
          <div className="mt-3 text-xs text-center opacity-70">
            User ID: {userId}
          </div>
        </div>

        {/* Подключение кошелька */}
        <div className="card bg-white/10 text-white">
          <div className="text-xl font-bold mb-3 text-center">{t.myWallet}</div>
          
          {walletAddress ? (
            <div className="space-y-3">
              <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-3">
                <div className="text-sm mb-1 font-semibold text-green-400">✅ {language === 'ru' ? 'Кошелёк подключен' : 'Wallet connected'}</div>
                <div className="text-xs break-all font-mono opacity-80">{walletAddress}</div>
              </div>

              <button
                onClick={handleDisconnectWallet}
                className="w-full py-2 rounded-lg font-semibold bg-red-600 hover:bg-red-700 transition-colors"
              >
                {t.disconnectWallet}
              </button>
            </div>
          ) : (
            <div className="text-center">
              <div className="mb-3 text-sm text-white/80">
                {language === 'ru' ? 'Подключите кошелёк для операций с балансом' : 'Connect wallet to manage your balance'}
              </div>
              <button
                onClick={handleConnectWallet}
                className="w-full py-3 rounded-lg font-semibold bg-brand-500 hover:bg-brand-600 text-white transition-colors shadow-soft"
              >
                {t.connectWallet}
              </button>
            </div>
          )}
        </div>

        {/* Сообщения об ошибках и успехе */}
        {error && (
          <div className="card bg-red-500/20 border border-red-500 text-white">
            <div className="font-semibold">❌ {error}</div>
          </div>
        )}

        {success && (
          <div className="card bg-green-500/20 border border-green-500 text-white">
            <div className="font-semibold">✅ {success}</div>
          </div>
        )}

        {/* Управление балансом - всегда отображается */}
        <div className="card bg-white/10 text-white">
          <div className="text-xl font-bold mb-4 text-center">{language === 'ru' ? '💳 Управление балансом' : '💳 Balance Management'}</div>
          
          {!walletAddress && (
            <div className="mb-4 bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 text-sm text-center">
              ⚠️ {language === 'ru' ? 'Подключите кошелёк для пополнения и вывода средств' : 'Connect wallet to deposit and withdraw'}
            </div>
          )}

          {/* Секция пополнения */}
          <div className="mb-6">
            <div className="text-lg font-semibold mb-3">{t.depositTitle}</div>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm opacity-80 mb-1 block">{t.depositAmount}</label>
                <input
                  type="number"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 rounded-lg bg-white/20 text-white placeholder-white/50 border border-white/30 focus:border-yellow-400 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !walletAddress}
                />
              </div>

              <button
                onClick={handleDeposit}
                disabled={loading || !depositAmount || parseFloat(depositAmount) < 0.5 || !walletAddress}
                className="w-full py-3 rounded-lg font-semibold bg-green-600 hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed transition-colors shadow-soft"
              >
                {loading ? (language === 'ru' ? '⏳ Обработка...' : '⏳ Processing...') : t.depositButton}
              </button>

              <div className="text-xs opacity-70 text-center">
                {t.minDeposit}
              </div>
            </div>
          </div>

          {/* Разделитель */}
          <div className="border-t border-white/20 my-6"></div>

          {/* Секция вывода */}
          <div>
            <div className="text-lg font-semibold mb-3">{t.withdrawTitle}</div>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm opacity-80 mb-1 block">{t.withdrawAmount}</label>
                <input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  max={balance}
                  className="w-full px-4 py-2 rounded-lg bg-white/20 text-white placeholder-white/50 border border-white/30 focus:border-yellow-400 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !walletAddress}
                />
              </div>

              <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 text-xs">
                <div className="font-semibold mb-1">⚠️ {t.withdrawFee}: 7%</div>
                <div className="opacity-80">
                  {withdrawAmount && parseFloat(withdrawAmount) > 0 
                    ? `${t.youReceive}: ${(parseFloat(withdrawAmount) * 0.93).toFixed(2)} TON` 
                    : (language === 'ru' ? 'Введите сумму для расчёта' : 'Enter amount to calculate')}
                </div>
              </div>

              <button
                onClick={handleWithdraw}
                disabled={loading || !withdrawAmount || parseFloat(withdrawAmount) < 1 || parseFloat(withdrawAmount) > balance || !walletAddress}
                className="w-full py-3 rounded-lg font-semibold bg-orange-600 hover:bg-orange-700 disabled:bg-gray-500 disabled:cursor-not-allowed transition-colors shadow-soft"
              >
                {loading ? (language === 'ru' ? '⏳ Обработка...' : '⏳ Processing...') : t.withdrawButton}
              </button>

              <div className="text-xs opacity-70 text-center">
                {t.minWithdraw}
              </div>
              
              <div className="text-xs opacity-70 text-center">
                {language === 'ru' ? `Доступно для вывода: ${balance.toFixed(2)} TON` : `Available for withdrawal: ${balance.toFixed(2)} TON`}
              </div>
              
              {/* Надпись о сроке вывода */}
              <div className="mt-3 bg-blue-500/20 border border-blue-500/50 rounded-lg p-3 text-center">
                <div className="text-sm font-semibold text-blue-300">
                  ⏰ {language === 'ru' ? 'Вывод в течение 24 часов' : 'Withdrawal within 24 hours'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceScreen;
